/** Platform admin: system screen (P9). Owned by the system screen; fil mirrors every key. */
export const system = {
  title: "System health",
  subtitle: "Live probes of platform services, queues, jobs and the database · refreshed every 30 seconds",
  scheduleMaintenance: "Schedule maintenance",
  summary: {
    allUp: "All {{count}} operational",
    mixed: "{{bad}} need attention · {{ok}} operational",
  },
  status: { up: "Up", degraded: "Degraded", down: "Down", unknown: "Unknown" },
  services: {
    title: "Services",
    caption: "Live probe status and response time",
    latency: "Latency",
    detail: "Detail",
    emptyTitle: "No services reported",
    emptyBody: "The health service returned no probes. Check that the API can reach its dependencies.",
  },
  queues: {
    title: "Queues",
    caption: "Background worker queues on Redis · last 24 hours",
    colQueue: "Queue",
    colStatus: "Status",
    colDepth: "Depth",
    colSucceeded: "Done (24h)",
    colOldest: "Oldest job",
    colFailed: "Failed (24h)",
    colActions: "Actions",
    states: { healthy: "Healthy", elevated: "Elevated", paused: "Paused" },
    pause: "Pause",
    resume: "Resume",
    retry: "Retry failed",
    scale: "Scale +{{count}}",
    paused: "{{queue}} paused · workers finish in-flight jobs",
    resumed: "{{queue}} resumed",
    retried_one: "{{count}} failed job re-enqueued on {{queue}}",
    retried_other: "{{count}} failed jobs re-enqueued on {{queue}}",
    noFailed: "No failed jobs in {{queue}}",
    scaled: "Scale-up of {{count}} workers requested for {{queue}}",
    elevated: "{{queue}} is elevated: {{depth}} jobs waiting, {{failed}} failed in 24h. Retry failed jobs or request more workers.",
    actionsFor: "Actions for {{queue}}",
    emptyTitle: "No queues registered",
    emptyBody: "Worker queues appear here once the worker pool reports in.",
    descriptions: {
      ingest_parse: "Document parsing, layout and table extraction",
      ingest_ocr: "OCR for scanned PDFs and images",
      ingest_enrich: "Context generation for chunks",
      ingest_embed: "Embeddings for new and changed chunks",
      crawl: "Website crawls and scheduled re-syncs",
      notify: "Email and push notifications",
      webhooks: "Outbound webhook deliveries",
      eval: "Evaluation suites (LLM-as-judge)",
      default: "General background tasks",
    },
  },
  jobs: {
    title: "Live job feed",
    pause: "Pause feed",
    resume: "Resume feed",
    pausedNote: "Feed paused",
    states: { succeeded: "Done", running: "Running", failed: "Failed", queued: "Queued", cancelled: "Cancelled" },
    stage: "Stage: {{stage}}",
    attempts_one: "{{count}} attempt",
    attempts_other: "{{count}} attempts",
    duration: "{{ms}} ms",
    untitled: "Untitled source",
    emptyTitle: "No recent jobs",
    emptyBody: "Ingestion jobs show up here as tenants upload or sync knowledge.",
  },
  latency: {
    title: "Latency",
    caption: "Last {{hours}}h · {{count}} samples",
    metricLabel: "Latency metric",
    metrics: { ttft: "Time to first token", retrieval: "Retrieval" },
    p50: "p50",
    p95: "p95",
    slo: "SLO p95",
    noSlo: "No SLO set",
    sloMeter: "p95 against the SLO",
    withinSlo: "Within SLO",
    overSlo: "Over SLO",
    noSamples: "No samples in this window",
    noSamplesBody: "Latency appears once traffic is traced in the last {{hours}} hours.",
  },
  database: {
    title: "Database",
    engine: "PostgreSQL {{version}} · pgvector {{pgvector}}",
    engineNoVector: "PostgreSQL {{version}} · pgvector not installed",
    size: "DB size",
    chunkRows: "Chunk rows",
    vectorIndexes: "Vector indexes",
    hnsw: "HNSW",
    hnswPresent: "Present",
    hnswMissing: "Missing",
    connections: "Connections",
    connectionsValue: "{{used}} / {{max}}",
    tables: "Largest tables",
    colTable: "Table",
    colRows: "Live rows",
    colDead: "Dead rows",
    colSize: "Size",
    colVacuum: "Last autovacuum",
    never: "Never",
    connectionsWarn: "Connections above 80% of the limit — check pooling before traffic grows.",
  },
  incidents: {
    title: "Incidents & maintenance",
    declare: "Declare incident",
    scheduled: "Scheduled: {{title}}",
    window: "{{start}} · {{duration}}",
    impacts: {
      none: "No downtime expected",
      read_only: "Brief read-only window (chat works, uploads paused)",
      full_outage: "Full outage — widget shows offline message",
    },
    services: "Affects {{services}}",
    edit: "Edit",
    cancel: "Cancel window",
    cancelled: "Maintenance cancelled",
    severity: { sev1: "SEV-1", sev2: "SEV-2", sev3: "SEV-3" },
    states: { investigating: "Investigating", monitoring: "Monitoring", resolved: "Resolved" },
    started: "Started {{when}}",
    lasted: "{{duration}}",
    fix: "Fix: {{fix}}",
    postmortem: "Post-mortem",
    markMonitoring: "Mark monitoring",
    resolve: "Resolve",
    resolved: "{{code}} resolved",
    monitoring: "{{code}} moved to monitoring",
    emptyTitle: "No incidents or maintenance",
    emptyBody: "Declared incidents and scheduled maintenance windows appear here.",
  },
  durations: { "15": "15 min", "30": "30 min", "60": "1 hour", "120": "2 hours" },
  elapsed: { minutes: "{{m}} min", hours: "{{h}}h {{m}}m", days: "{{d}}d {{h}}h", seconds: "{{s}}s" },
  maintenanceDialog: {
    title: "Schedule maintenance",
    editTitle: "Edit maintenance",
    description: "Tenants see the window in their console and the notifications you choose below.",
    editDescription: "Impact, services and notifications can't change once a window is scheduled.",
    titleLabel: "Title",
    titlePlaceholder: "e.g. PostgreSQL minor upgrade",
    date: "Date",
    start: "Start (PHT)",
    duration: "Duration",
    impact: "Impact",
    services: "Affected services",
    notification: "Tenant notification",
    notify: {
      email: "Email all tenant owners & admins",
      banner_72h: "In-app banner in tenant console from 72h before",
      status_page: "Post to the public status page",
    },
    message: "Message to tenants",
    messagePlaceholder: "What changes, when, and whether tenants need to do anything.",
    submit: "Schedule & notify",
    save: "Save changes",
    scheduled: "Maintenance scheduled · tenants will be notified",
    updated: "Maintenance window updated",
    titleRequired: "Give the window a title.",
    dateRequired: "Pick a date and start time.",
    pastStart: "Pick a start time in the future.",
  },
  incidentDialog: {
    title: "Declare incident",
    description: "Opens an incident on the platform timeline. Resolve it from the Incidents card.",
    summary: "Summary",
    summaryPlaceholder: "e.g. Embedding queue backlog delaying uploads",
    summaryRequired: "Describe the incident in a few words.",
    severity: "Severity",
    severities: { sev3: "SEV-3 · minor", sev2: "SEV-2 · degraded", sev1: "SEV-1 · outage" },
    lead: "Incident lead",
    you: "{{name}} (you)",
    impact: "Impact",
    impactPlaceholder: "Who is affected and how",
    statusPage: "Post to status page",
    submit: "Declare",
    declared: "Incident {{code}} declared",
  },
} as const;
